import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback } from 'react';

import { type UUID } from '~/types/common';

import {
  fetchUser,
  fetchUsers,
  queryKeysUser,
  useGetUserUtils,
  type UserListItem,
} from '~/data/user';

import { type Option, OptionPrimarySecondary } from '@vestigas/ui/molecules';

import { SelectServerDriven } from './SelectServerDriven';
import { createGetItemData, createLoadOptions } from './utils';

const getOptionLabel = ({ email }) => email;

const defaultFormatOptionLabel = (option: Option<UserListItem>) => (
  <OptionPrimarySecondary
    primaryInformation={option?.data?.email}
    secondaryInformation={[option?.data?.firstName, option?.data?.lastName]
      .filter(Boolean)
      .join(' ')}
  />
);

/**
 * Entry offered ahead of the fetched users. Its id is emitted like a user id,
 * so callers give it a value no user id can take.
 */
export type UserStaticOption = {
  readonly id: string;
  readonly label: string;
};

const NO_STATIC_OPTIONS: readonly UserStaticOption[] = [];

/** Shapes a static entry like a fetched user, so no renderer needs a special case. */
function toStaticUser({ id, label }: UserStaticOption): UserListItem {
  return {
    companyId: '',
    email: label,
    firstName: '',
    id,
    isActive: true,
    isSynced: false,
    lastActiveDatetime: '',
    lastName: '',
    position: '',
  };
}

type P = {
  readonly filterActive?: boolean | null;
  readonly formatOptionLabel?: (
    option: Option<UserListItem>,
  ) => React.ReactNode;
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isMultiSelect?: boolean;
  readonly isRemovableValue?: (data: Option<UserListItem>) => boolean;
  readonly onChange: (value: Array<{ id: string }> | { id: string }) => void;
  readonly placeholder?: string;
  readonly staticOptions?: readonly UserStaticOption[];
  readonly value: string | string[];
} & ComponentStyling;

export const MultiSelectUsers = ({
  className,
  filterActive = true,
  formatOptionLabel = defaultFormatOptionLabel,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = true,
  isRemovableValue,
  onChange,
  placeholder = 'Benutzer',
  staticOptions = NO_STATIC_OPTIONS,
  style,
  value,
}: P) => {
  const queryClient = useQueryClient();

  const { UserUtils } = useGetUserUtils();

  /**
   * Loads the options for the multi-select dropdown.
   * @param {string} searchString - The search string entered by the user.
   * @param {unknown} loadedOptions - The already loaded options.
   * @param {{ page: number }} - The current page number.
   */
  const loadOptions = createLoadOptions<UserListItem>({
    createQueryKey: ({ page, ...queryParams }) =>
      queryKeysUser.getAll(queryParams),
    async fetchItems({ page, ...queryParams }) {
      const { data, hasNextPage } = await fetchUsers(queryParams);

      return {
        // Only the first page carries the static entries, so scrolling into
        // later pages does not repeat them.
        data:
          queryParams.offset === 0
            ? [...staticOptions.map(toStaticUser), ...data]
            : data,
        hasNextPage,
      };
    },
    filterActive,
    getOptionLabel,
    queryClient,
  });

  /**
   * Gets user data for a user with a given userId.
   * Attempts to get it from the query cache and fetches it from the API
   * if the user data is not in the cache.
   * @param {string} userId - The ID of the user to get.
   */
  const getItemData = createGetItemData<UserListItem>({
    createQueryKey: (userId: UUID) => queryKeysUser.get(userId),
    async fetchItem(userId) {
      const staticOption = staticOptions.find(({ id }) => id === userId);

      return staticOption ? toStaticUser(staticOption) : fetchUser(userId);
    },
    queryClient,
    queryKeyBase: [queryKeysUser.base()],
  });

  // This is just a glue function b/c PermissionGrantMultiPicker currently expects to pick the ID from an object in its onChange function. Should be refactored.
  const handleChange = useCallback(
    (newValues: string[] | string) => {
      onChange(
        Array.isArray(newValues)
          ? newValues.map((id) => ({ id }))
          : { id: newValues },
      );
    },
    [onChange],
  );

  return (
    <SelectServerDriven<UserListItem>
      className={className}
      formatOptionLabel={formatOptionLabel}
      getItemData={getItemData}
      getOptionLabel={getOptionLabel}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isItemSelectionAllowed={UserUtils.isUserReadAllowedUser}
      isMultiSelect={isMultiSelect}
      isRemovableValue={isRemovableValue}
      loadOptions={loadOptions}
      placeholder={placeholder}
      style={style}
      value={value}
      onChange={handleChange}
    />
  );
};
